import { animate } from "motion";

const libraries = {};

class Arcade {
  constructor(canvas) {
    this.canvas = canvas;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.composer = null;
    this.machines = [];
    this.videosReady = false;
    this.activeIndex = -1;
    this.gap = 15;
    this.initialFocusOffset = -15;
    this.initialCameraOffsetX = 80;
    this.cameraOffsetX = 0;
    this.cameraY = 7.5;
    this.lookAtY = 3.5;
    this.cameraZ = 60;
    this.dimAmount = 0.25;
    this.lookAtX = this.initialFocusOffset;
    this.clock = new libraries.THREE.Clock();
    this.rafId = null;
    this.cameraAnimation = null;
    this.dimAnimations = [];
    this.raycaster = new libraries.THREE.Raycaster();
    this.pointer = new libraries.THREE.Vector2();
    this.pointerDown = null;
    this.onResize = this.onWindowResize.bind(this);
    this.onLetsPlay = this.handleLetsPlay.bind(this);
    this.onPrev = this.handlePrev.bind(this);
    this.onNext = this.handleNext.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);

    // this.gui = new libraries.GUI();
    // this.setupGui();
    this.setupInteractions();
    this.updateActiveClasses();
    this.loadAssets();
  }

  setupGui() {
    this.gui
      .add(this, "gap", 5, 50, 0.5)
      .name("Gap")
      .onChange(() => {
        this.updateMachinePositions();
        this.focusCamera();
      });

    this.gui
      .add(this, "initialFocusOffset", -30, 30, 0.5)
      .name("Initial focus offset")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "initialCameraOffsetX", -200, 200, 0.5)
      .name("Initial camera offset X")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "cameraOffsetX", -20, 20, 0.5)
      .name("Camera offset X")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "cameraY", -40, 40, 0.5)
      .name("Camera Y")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "lookAtY", -40, 40, 0.5)
      .name("Look at Y")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "cameraZ", 10, 200, 0.5)
      .name("Zoom")
      .onChange(() => this.focusCamera());

    this.gui
      .add(this, "dimAmount", 0, 1, 0.01)
      .name("Dim amount")
      .onChange(() => this.updateMachineBrightness(false));
  }

  setupInteractions() {
    this.letsPlayButton = document.querySelector("#lets-play");
    this.prevButton = document.querySelector(".arcade-nav--prev");
    this.nextButton = document.querySelector(".arcade-nav--next");

    this.letsPlayButton?.addEventListener("click", this.onLetsPlay);
    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    window.addEventListener("keydown", this.onKeyDown);

    Array.from(
      document.querySelectorAll(".arcade-machine-info--close"),
    ).forEach((close) => {
      close.addEventListener("click", () => this.setActiveIndex(-1));
    });
  }

  setupRaycasting() {
    const el = this.renderer?.domElement;
    if (!el) return;
    el.addEventListener("pointerdown", this.onPointerDown);
    el.addEventListener("pointerup", this.onPointerUp);
  }

  handleLetsPlay(event) {
    event.preventDefault();
    if (this.activeIndex >= 0) return;
    this.setActiveIndex(0);
  }

  handlePrev(event) {
    event.preventDefault();
    this.setActiveIndex(this.activeIndex - 1);
  }

  handleNext(event) {
    event.preventDefault();
    this.setActiveIndex(this.activeIndex + 1);
  }

  handleKeyDown(event) {
    if (event.key !== "Escape") return;
    if (this.activeIndex < 0) return;
    this.setActiveIndex(-1);
  }

  handlePointerDown(event) {
    this.pointerDown = { x: event.clientX, y: event.clientY };
  }

  handlePointerUp(event) {
    if (!this.pointerDown || !this.camera || !this.renderer) {
      this.pointerDown = null;
      return;
    }

    const dx = event.clientX - this.pointerDown.x;
    const dy = event.clientY - this.pointerDown.y;
    this.pointerDown = null;

    if (this.activeIndex >= 0 && Math.abs(dx) >= 50) {
      this.setActiveIndex(
        this.activeIndex + (dx > 0 ? -1 : 1),
      );
      return;
    }

    // Ignore small drags
    if (Math.hypot(dx, dy) > 5) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(
      this.machines.map((machine) => machine.group),
      true,
    );

    if (!hits.length) return;

    let object = hits[0].object;
    while (object) {
      if (typeof object.userData.machineIndex === "number") {
        this.setActiveIndex(object.userData.machineIndex);
        return;
      }
      object = object.parent;
    }
  }

  updateMachinePositions() {
    this.machines.forEach((machine, i) => {
      machine.group.position.x = i * this.gap;
    });
  }

  getFocusX() {
    if (this.activeIndex < 0) return this.initialFocusOffset;
    return this.activeIndex * this.gap;
  }

  getCameraX() {
    const focusX = this.getFocusX();
    if (this.activeIndex < 0) return focusX + this.initialCameraOffsetX;
    return focusX + this.cameraOffsetX;
  }

  setActiveIndex(index) {
    if (index < -1 || index >= this.machines.length) return;
    if (index === this.activeIndex) return;

    this.activeIndex = index;
    this.animateCameraTo();
    this.updateMachineBrightness(true);
    this.updateActiveClasses();
  }

  updateActiveClasses() {
    document
      .querySelector(".arcade-intro")
      ?.classList.toggle("is-active", this.activeIndex < 0);

    this.machines.forEach((machine, i) => {
      machine.info?.classList.toggle("is-active", i === this.activeIndex);
    });

    // Before machines are built, still clear any leftover active states
    if (!this.machines.length) {
      document.querySelectorAll(".arcade-machine-info").forEach((el) => {
        el.classList.remove("is-active");
      });
    }

    this.updateNavButtons();
  }

  updateNavButtons() {
    const lastIndex = this.machines.length - 1;
    if (this.prevButton) {
      this.prevButton.disabled = this.activeIndex <= -1;
    }
    if (this.nextButton) {
      this.nextButton.disabled = lastIndex < 0 || this.activeIndex >= lastIndex;
    }
  }

  getTargetBrightness(index) {
    if (this.activeIndex < 0) return this.dimAmount;
    return index === this.activeIndex ? 1 : this.dimAmount;
  }

  setMachineBrightness(machine, brightness) {
    machine.brightness = brightness;

    machine.model.traverse((child) => {
      if (!child.isMesh) return;

      child.material.color
        .copy(child.userData.baseColor)
        .multiplyScalar(brightness);
    });

    machine.screen.material.color.setScalar(brightness);
    machine.screen.material.emissive.setScalar(brightness);
    machine.screen.material.emissiveIntensity = brightness;
  }

  updateMachineBrightness(shouldAnimate = false) {
    this.machines.forEach((machine, i) => {
      const target = this.getTargetBrightness(i);
      this.dimAnimations[i]?.stop();

      if (!shouldAnimate) {
        this.setMachineBrightness(machine, target);
        return;
      }

      const from = { brightness: machine.brightness ?? 1 };
      this.dimAnimations[i] = animate(
        from,
        { brightness: target },
        {
          duration: 1.2,
          ease: [0.22, 1, 0.36, 1],
          onUpdate: () => {
            this.setMachineBrightness(machine, from.brightness);
          },
        },
      );
    });
  }

  focusCamera() {
    if (!this.camera) return;

    this.cameraAnimation?.stop();
    this.cameraAnimation = null;

    this.lookAtX = this.getFocusX();
    this.camera.position.set(this.getCameraX(), this.cameraY, this.cameraZ);
    this.camera.lookAt(this.lookAtX, this.lookAtY, 0);
  }

  animateCameraTo() {
    if (!this.camera) return;

    this.cameraAnimation?.stop();

    const from = {
      cameraX: this.camera.position.x,
      lookX: this.lookAtX,
      y: this.camera.position.y,
      z: this.camera.position.z,
      lookY: this.lookAtY,
    };

    const to = {
      cameraX: this.getCameraX(),
      lookX: this.getFocusX(),
      y: this.cameraY,
      z: this.cameraZ,
      lookY: this.lookAtY,
    };

    this.cameraAnimation = animate(from, to, {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: () => {
        this.lookAtX = from.lookX;
        this.camera.position.set(from.cameraX, from.y, from.z);
        this.camera.lookAt(from.lookX, from.lookY, 0);
      },
    });
  }

  loadAssets() {
    const loader = new libraries.GLTFLoader();

    loader.load(
      `${THEME_DIR}/assets/gltf/arcade3.gltf?v=3`,
      (gltf) => {
        this.init(gltf);
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            console.log(
              child.name,
              child.material.name,
              child.material.color,
              child.material.emissive,
            );
          }
        });
      },
      undefined,
      (error) => {
        console.error(error);
      },
    );
  }

  init(gltf) {
    this.renderer = this.setupRenderer();
    this.camera = this.setupCamera();
    this.scene = new libraries.THREE.Scene();

    this.setupGames(this.scene, this.setupArcadeMaterials(gltf));
    this.setupRaycasting();
    this.focusCamera();
    this.updateActiveClasses();

    window.addEventListener("resize", this.onResize);
    this.onWindowResize();
    this.animate();
  }

  setupRenderer() {
    const { THREE } = libraries;
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      transparent: false,
    });
    const { width, height } = this.getSize();

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 1);
    renderer.setSize(width, height);
    this.canvas.appendChild(renderer.domElement);
    return renderer;
  }

  setupCamera() {
    const { THREE } = libraries;
    const { width, height } = this.getSize();
    const camera = new THREE.PerspectiveCamera(40, width / height, 1, 1000);
    this.lookAtX = this.getFocusX();
    camera.position.set(this.getCameraX(), this.cameraY, this.cameraZ);
    camera.lookAt(this.lookAtX, this.lookAtY, 0);
    return camera;
  }

  setupArcadeMaterials(gltf) {
    const { THREE } = libraries;

    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        if (child.name === "Curve001" || child.name === "Curve") {
          child.material = new THREE.MeshBasicMaterial({
            color: 0x000000,
          });
        } else {
          child.material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
          });
        }
      }
    });

    return gltf;
  }

  createVideoElement(src) {
    const video = document.createElement("video");
    video.src = src;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.style.display = "none";
    document.body.appendChild(video);
    return video;
  }

  setupGames(scene, gltf) {
    const { THREE } = libraries;

    gltf.scene.scale.set(8, 8, 8);

    const gameInfos = Array.from(
      document.querySelectorAll(".arcade-machine-info[data-game-video]"),
    );

    const games = gameInfos.map((info) => ({
      videoElement: this.createVideoElement(info.dataset.gameVideo),
      info,
    }));

    games.forEach((game, i) => {
      const screenTexture = new THREE.VideoTexture(game.videoElement);
      const screenGeometry = new THREE.PlaneGeometry(8, 6);
      const screenMaterial = new THREE.MeshStandardMaterial({
        map: screenTexture,
        emissive: 0xffffff,
        emissiveMap: screenTexture,
        emissiveIntensity: 1.0,
      });
      const screen = new THREE.Mesh(screenGeometry, screenMaterial);

      const screenScale = 0.9;
      screen.scale.set(screenScale, screenScale, screenScale);
      screen.position.z = 3;
      screen.position.y = 7;
      screen.position.x = -0.3;
      screen.rotation.x = Math.PI * -0.07;

      const machine = {
        texture: screenTexture,
        group: new THREE.Group(),
        model: gltf.scene.clone(true),
        screen,
        videoElement: game.videoElement,
        info: game.info,
        brightness: this.dimAmount,
        i,
      };

      machine.model.traverse((child) => {
        if (!child.isMesh || !child.material) return;

        child.material = child.material.clone();

        child.userData.isDark =
          child.name === "Curve001" || child.name === "Curve";

        child.userData.baseColor = child.material.color.clone();
      });

      machine.model.position.x = -2.2;
      machine.model.position.z = 2;

      machine.group.userData.machineIndex = i;
      machine.group.add(machine.screen);
      machine.group.add(machine.model);
      machine.group.position.x = i * this.gap;
      machine.group.rotation.y = 0;

      this.setMachineBrightness(machine, this.dimAmount);
      this.machines.push(machine);
      scene.add(machine.group);
    });
  }

  getSize() {
    return {
      width: this.canvas.clientWidth || window.innerWidth,
      height: this.canvas.clientHeight || window.innerHeight,
    };
  }

  onWindowResize() {
    if (!this.camera || !this.renderer) return;

    const { width, height } = this.getSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  animate() {
    this.rafId = requestAnimationFrame(() => this.animate());

    if (!this.videosReady) {
      if (
        this.machines.some(
          (machine) =>
            machine.videoElement.readyState <
            HTMLVideoElement.HAVE_CURRENT_DATA,
        )
      ) {
        return;
      }

      this.videosReady = true;
      this.machines.forEach((machine) => {
        machine.videoElement.play();
        machine.texture.needsUpdate = true;
      });
    }

    const { width, height } = this.getSize();
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setViewport(0, 0, width, height);
    this.renderer.render(this.scene, this.camera);
    // this.composer?.render(this.clock.getDelta());
  }

  destroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.onResize);
    this.letsPlayButton?.removeEventListener("click", this.onLetsPlay);
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    window.removeEventListener("keydown", this.onKeyDown);
    this.renderer?.domElement?.removeEventListener(
      "pointerdown",
      this.onPointerDown,
    );
    this.renderer?.domElement?.removeEventListener(
      "pointerup",
      this.onPointerUp,
    );
    this.cameraAnimation?.stop();
    this.dimAnimations.forEach((animation) => animation?.stop());
    this.dimAnimations = [];

    this.machines.forEach((machine) => {
      machine.videoElement.pause();
      machine.videoElement.remove();
      machine.texture.dispose();
    });

    this.gui?.destroy();
    this.renderer?.dispose();
    this.renderer?.domElement?.remove();

    this.machines = [];
  }
}

export default {
  async init() {
    const canvas = document.querySelector("#arcade-canvas");
    if (!canvas) return;
    this.bootstrap(canvas);
  },

  async bootstrap(canvas) {
    if (libraries.THREE) {
      return this.setup(canvas);
    }

    const [
      THREE,
      { GUI },
      { GLTFLoader },
      {
        BloomEffect,
        ScanlineEffect,
        EffectComposer,
        EffectPass,
        RenderPass,
        GlitchEffect,
      },
    ] = await Promise.all([
      import("three"),
      import("lil-gui"),
      import("three/examples/jsm/loaders/GLTFLoader.js"),
      import("postprocessing"),
    ]);

    Object.assign(libraries, {
      THREE,
      GUI,
      GLTFLoader,
      BloomEffect,
      ScanlineEffect,
      EffectComposer,
      EffectPass,
      RenderPass,
      GlitchEffect,
    });

    return this.setup(canvas);
  },

  setup(canvas) {
    this.destroy();
    this.arcade = new Arcade(canvas);
  },

  destroy() {
    this.arcade?.destroy();
    this.arcade = null;
  },
};
